import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Camera, FileText, Shield, Upload, Trash2, Download, Calendar, Mail, Briefcase, Building2, Users, Hash, Heart, Award, Loader2, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyProfile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({ doc_type: 'health', provider: '', policy_number: '', valid_until: '' });
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [uploadingInsurance, setUploadingInsurance] = useState(false);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ category: 'Aadhaar Card', doc_name: '' });
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const ud = localStorage.getItem('user');
    if (ud) setCurrentUser(JSON.parse(ud));
  }, []);

  const targetId = userId || currentUser?.id;
  const isOwnProfile = !userId || userId === currentUser?.id;
  const isAdmin = currentUser && ['admin', 'admin_support'].includes(currentUser.role);
  const isTeamLeader = currentUser && currentUser.role === 'team_leader';
  // Document upload is restricted to Admin / Team Leader only.
  // Recruiters can still view their own documents but cannot upload or delete them.
  const canManageDocs = isAdmin || isTeamLeader;

  useEffect(() => {
    if (targetId) fetchProfile();
  }, [targetId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile/${targetId}`, { headers: getHeaders() });
      setProfile(res.data);
    } catch (e) { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/profile/photo`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      setProfile(prev => ({ ...prev, user: { ...prev.user, photo_url: res.data.photo_url } }));
      toast.success('Photo updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingPhoto(false); }
  };

  const handleInsuranceUpload = async () => {
    if (!insuranceFile) return toast.error('Please select a PDF file');
    setUploadingInsurance(true);
    const formData = new FormData();
    formData.append('file', insuranceFile);
    formData.append('doc_type', insuranceForm.doc_type);
    formData.append('provider', insuranceForm.provider);
    formData.append('policy_number', insuranceForm.policy_number);
    formData.append('valid_until', insuranceForm.valid_until);
    try {
      await axios.post(`${API}/profile/${targetId}/insurance`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Insurance document uploaded');
      setShowInsuranceDialog(false);
      setInsuranceFile(null);
      setInsuranceForm({ doc_type: 'health', provider: '', policy_number: '', valid_until: '' });
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingInsurance(false); }
  };

  const handleDeleteInsurance = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/profile/${targetId}/insurance/${docId}`, { headers: getHeaders() });
      toast.success('Document deleted');
      fetchProfile();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleDocUpload = async () => {
    if (!docFile) return toast.error('Please select a file');
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', docFile);
    formData.append('category', docForm.category);
    formData.append('doc_name', docForm.doc_name || docFile.name);
    try {
      await axios.post(`${API}/profile/${targetId}/documents`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setShowDocDialog(false);
      setDocFile(null);
      setDocForm({ category: 'Aadhaar Card', doc_name: '' });
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDoc(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/profile/${targetId}/documents/${docId}`, { headers: getHeaders() });
      toast.success('Document deleted');
      fetchProfile();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={40} className="animate-spin text-blue-400" /></div>;
  if (!profile) return <div className="text-center py-20 text-slate-500">Profile not found</div>;

  const { user, team, insurance_documents, personal_documents } = profile;
  const photoSrc = user.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : null;

  return (
    <div className="max-w-4xl mx-auto" data-testid="my-profile-page">
      {/* Profile Header */}
      <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 rounded-2xl p-8 mb-6 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h20v20H0zM20 20h20v20H20z\' fill=\'%23fff\' fill-opacity=\'0.1\'/%3E%3C/svg%3E")' }} />
        <div className="relative flex items-start gap-6">
          <div className="relative group">
            <div className="w-28 h-28 rounded-2xl overflow-hidden border-4 border-white/30 shadow-lg bg-white/20 flex items-center justify-center">
              {photoSrc ? (
                <img src={photoSrc} alt={user.name} className="w-full h-full object-cover" data-testid="profile-photo" />
              ) : (
                <UserCircle size={64} className="text-white/60" />
              )}
            </div>
            {isOwnProfile && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-2 -right-2 w-9 h-9 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 transition-colors"
                  data-testid="change-photo-btn"
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <Loader2 size={16} className="animate-spin text-blue-600" /> : <Camera size={16} className="text-blue-600" />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </>
            )}
          </div>
          <div className="flex-1 text-white">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="profile-name">{user.name}</h1>
            <p className="text-blue-100 text-lg mt-1">{user.designation || user.role}</p>
            {user.department && <p className="text-blue-200 text-sm mt-0.5">{user.department}</p>}
            <div className="flex gap-3 mt-4 flex-wrap">
              {user.employee_number && (
                <span className="px-3 py-1 bg-white/15 backdrop-blur rounded-lg text-xs font-bold flex items-center gap-1.5"><Hash size={12} />{user.employee_number}</span>
              )}
              <span className="px-3 py-1 bg-white/15 backdrop-blur rounded-lg text-xs font-bold flex items-center gap-1.5">
                <Shield size={12} />{user.role?.replace('_', ' ').toUpperCase()}
              </span>
              {team && (
                <span className="px-3 py-1 bg-white/15 backdrop-blur rounded-lg text-xs font-bold flex items-center gap-1.5"><Users size={12} />{team.name}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <InfoCard icon={Mail} label="Email" value={user.email} testId="profile-email" />
        <InfoCard icon={Briefcase} label="Designation" value={user.designation} testId="profile-designation" />
        <InfoCard icon={Building2} label="Department" value={user.department} testId="profile-department" />
        <InfoCard icon={Users} label="Team" value={team?.name} testId="profile-team" />
        <InfoCard icon={Calendar} label="Date of Joining" value={user.date_of_joining ? new Date(user.date_of_joining).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null} testId="profile-doj" />
        <InfoCard icon={Calendar} label="Date of Birth" value={user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null} testId="profile-dob" />
        {user.marriage_anniversary && <InfoCard icon={Heart} label="Marriage Anniversary" value={new Date(user.marriage_anniversary).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} testId="profile-ma" />}
        {user.date_of_exit && <InfoCard icon={Calendar} label="Exit Date" value={new Date(user.date_of_exit).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} testId="profile-exit" />}
      </div>

      {/* Insurance Documents */}
      <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm" data-testid="insurance-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Insurance Documents</h2>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowInsuranceDialog(true)} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold" data-testid="upload-insurance-btn">
              <Upload size={14} className="mr-1.5" />Upload
            </Button>
          )}
        </div>
        {insurance_documents?.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold text-blue-900">Type</TableHead>
                <TableHead className="font-bold text-blue-900">Provider</TableHead>
                <TableHead className="font-bold text-blue-900">Policy #</TableHead>
                <TableHead className="font-bold text-blue-900">Valid Until</TableHead>
                <TableHead className="font-bold text-blue-900">File</TableHead>
                {isAdmin && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {insurance_documents.map(doc => (
                <TableRow key={doc.id} data-testid={`insurance-row-${doc.id}`}>
                  <TableCell><span className={`px-2.5 py-1 text-xs font-bold rounded-lg ${doc.doc_type === 'health' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>{doc.doc_type === 'health' ? 'Health' : 'Accident'}</span></TableCell>
                  <TableCell className="text-sm font-medium">{doc.provider || '-'}</TableCell>
                  <TableCell className="text-sm font-mono">{doc.policy_number || '-'}</TableCell>
                  <TableCell className="text-sm">{doc.valid_until || '-'}</TableCell>
                  <TableCell>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-semibold" data-testid={`download-insurance-${doc.id}`}>
                      <Download size={14} />{doc.file_name || 'View'}
                    </a>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteInsurance(doc.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-insurance-${doc.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No insurance documents uploaded yet</p>
          </div>
        )}
      </div>

      {/* Personal Documents */}
      <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-sm mt-6" data-testid="personal-docs-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Personal Documents</h2>
          </div>
          {canManageDocs && (
            <Button onClick={() => setShowDocDialog(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold" data-testid="upload-doc-btn">
              <Upload size={14} className="mr-1.5" />Upload Document
            </Button>
          )}
        </div>
        {personal_documents?.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold text-blue-900">Category</TableHead>
                <TableHead className="font-bold text-blue-900">Document Name</TableHead>
                <TableHead className="font-bold text-blue-900">Uploaded</TableHead>
                <TableHead className="font-bold text-blue-900">File</TableHead>
                {canManageDocs && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {personal_documents.map(doc => (
                <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                  <TableCell><span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-indigo-100 text-indigo-800">{doc.category}</span></TableCell>
                  <TableCell className="text-sm font-medium">{doc.doc_name || doc.file_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-semibold" data-testid={`download-doc-${doc.id}`}>
                      <Download size={14} />{doc.file_name || 'View'}
                    </a>
                  </TableCell>
                  {canManageDocs && (
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No personal documents uploaded yet</p>
          </div>
        )}
      </div>

      {/* Document Upload Dialog */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-bold text-blue-900">Upload Personal Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Document Category</Label>
              <select value={docForm.category} onChange={e => setDocForm({ ...docForm, category: e.target.value })} className="flex h-9 w-full rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm mt-1" data-testid="doc-category-select">
                {["Aadhaar Card", "PAN Card", "Passport", "Driving License", "Voter ID", "Offer Letter", "Appointment Letter", "Experience Letter", "Relieving Letter", "Salary Slips", "Bank Statement", "Educational Certificates", "Other"].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Document Name (optional)</Label>
              <Input value={docForm.doc_name} onChange={e => setDocForm({ ...docForm, doc_name: e.target.value })} placeholder="e.g., Aadhaar Front & Back" className="mt-1 border-blue-200" data-testid="doc-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">File (PDF, DOC, JPG, PNG — max 10MB)</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setDocFile(e.target.files?.[0])} className="mt-1 border-blue-200" data-testid="doc-file-input" />
            </div>
            <Button onClick={handleDocUpload} disabled={uploadingDoc} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg" data-testid="doc-upload-submit">
              {uploadingDoc ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
              Upload Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Upload Dialog */}
      <Dialog open={showInsuranceDialog} onOpenChange={setShowInsuranceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-bold text-blue-900">Upload Insurance Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Document Type</Label>
              <select value={insuranceForm.doc_type} onChange={e => setInsuranceForm({ ...insuranceForm, doc_type: e.target.value })} className="flex h-9 w-full rounded-lg border border-blue-200 bg-white px-3 py-1 text-sm mt-1" data-testid="insurance-type-select">
                <option value="health">Health Insurance</option>
                <option value="accident">Accident Insurance</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Provider</Label>
              <Input value={insuranceForm.provider} onChange={e => setInsuranceForm({ ...insuranceForm, provider: e.target.value })} placeholder="e.g., ICICI Lombard" className="mt-1 border-blue-200" data-testid="insurance-provider" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Policy Number</Label>
              <Input value={insuranceForm.policy_number} onChange={e => setInsuranceForm({ ...insuranceForm, policy_number: e.target.value })} placeholder="e.g., POL-12345" className="mt-1 border-blue-200" data-testid="insurance-policy-num" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Valid Until</Label>
              <Input type="date" value={insuranceForm.valid_until} onChange={e => setInsuranceForm({ ...insuranceForm, valid_until: e.target.value })} className="mt-1 border-blue-200" data-testid="insurance-valid-until" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">PDF File</Label>
              <Input type="file" accept=".pdf" onChange={e => setInsuranceFile(e.target.files?.[0])} className="mt-1 border-blue-200" data-testid="insurance-file-input" />
            </div>
            <Button onClick={handleInsuranceUpload} disabled={uploadingInsurance} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg" data-testid="insurance-upload-submit">
              {uploadingInsurance ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
              Upload Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="bg-white border border-slate-100 rounded-xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-blue-600" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
